/* iTechVista — keyboard access for click-handled elements
   ---------------------------------------------------------------------------
   The tool and simulator sections carry roughly a hundred elements that are
   clickable but are not buttons — <div onclick="...">, <span onclick="...">.
   They work with a mouse and are completely unreachable with a keyboard, which
   is a WCAG 2.1.1 failure and, on a page like the tool hub, means a keyboard
   user cannot use the tool at all.

   Rewriting each one as a real <button> is the correct fix and the risky one:
   these are working sections with their own CSS hanging off the tag names, and
   a <div> and a <button> lay out very differently. This does the next best
   thing without touching the markup — it gives those elements the role,
   focusability and key handling a button already has.

   Deliberate limits:
     * Only elements with an inline `onclick` ATTRIBUTE are upgraded. That is
       an unambiguous declaration of intent by the author. Elements wired up
       with addEventListener are invisible here, and guessing at them would
       mean making things focusable that were never meant to be.
     * Anything already interactive or already given a role/tabindex is left
       alone, so nothing is overridden.
     * If this script never loads, the page behaves exactly as it does today.
       It only ever adds capability.

   Much of this markup is built by the sections' own scripts after load, so a
   MutationObserver picks up what arrives later. It is throttled through
   requestAnimationFrame — the SERP simulator can add several hundred nodes in
   one go, and re-scanning per node would be pointless work. */
(function () {
  'use strict';

  var NATIVE = 'a,button,input,select,textarea,summary,details,label,option,[contenteditable]';
  var pending = false;

  function upgrade(root) {
    var nodes = root.querySelectorAll('[onclick]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.itvKeyable) continue;
      // already reachable and activatable by keyboard — leave it alone
      if (el.matches(NATIVE) || el.closest(NATIVE)) continue;
      if (el.hasAttribute('role') || el.hasAttribute('tabindex')) continue;

      el.dataset.itvKeyable = '1';
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
    }
  }

  function schedule(root) {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () {
      pending = false;
      upgrade(root || document);
    });
  }

  /* One delegated listener rather than one per element: the set of elements
     changes constantly as the tools redraw, and rebinding each time would leak
     handlers. Space is cancelled on keydown so the page does not scroll, and
     fired on keyup, which is what a real button does. */
  document.addEventListener('keydown', function (e) {
    var el = e.target;
    if (!el || !el.dataset || !el.dataset.itvKeyable) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      el.click();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
    }
  });

  document.addEventListener('keyup', function (e) {
    var el = e.target;
    if (!el || !el.dataset || !el.dataset.itvKeyable) return;
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      el.click();
    }
  });

  function start() {
    upgrade(document);
    if ('MutationObserver' in window) {
      new MutationObserver(function () { schedule(document); })
        .observe(document.body, { childList: true, subtree: true });
    }

    /* The observer alone left eight controls behind on the SERP simulator.
       Those sections render their own markup from scripts that run on their
       own schedule, and a batch that lands while a scan is already queued can
       fall between the two. Two deferred sweeps close that window: one when
       subresources have finished, one shortly after for anything that builds
       itself on a timer. Cheap, and it makes the outcome deterministic
       instead of dependent on when a section happens to draw. */
    window.addEventListener('load', function () { upgrade(document); }, { once: true });
    setTimeout(function () { upgrade(document); }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
